import { useEffect, useRef, useState } from "react";
import { BsArrowsFullscreen, BsImage, BsPlusCircle, BsZoomIn, BsZoomOut } from "react-icons/bs";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import AerialMarker from "./AerialMarker";
import {
	AERIAL_TRANSFORM_MAX_SCALE,
	AERIAL_TRANSFORM_MIN_SCALE,
	getMarkerFocusTransformScale,
	getMarkerScreenPoint,
	getZoomCenteredTransformPosition,
	MARKER_FOCUS_ANIMATION_MS,
	pointerToNormalizedCoordinates,
} from "./aerialCoordinates";

const AerialCanvas = ({
	image,
	markers,
	fieldsById,
	selectedFieldId,
	skipMarkerFocus,
	aerialFocusRequestId,
	markerFocusRequestId,
	expandedFieldId,
	placementFieldId,
	readOnly,
	onSelectField,
	onConfirmMarkerSelect,
	onPlaceMarker,
	onPreviewMarker,
	onSaveMarker,
	onOpenImagePicker,
	onOpenImageRequest,
}) => {
	const imageReference = useRef(null);
	const viewportReference = useRef(null);
	const setTransformReference = useRef(null);
	const transformInstanceReference = useRef(null);
	const transformStateReference = useRef({ scale: 1, positionX: 0, positionY: 0 });
	const markersReference = useRef(markers);
	const imageForFocusReference = useRef(image);
	const suppressPointerFocusReference = useRef(false);
	const [scale, setScale] = useState(1);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [markerPointerActive, setMarkerPointerActive] = useState(false);
	markersReference.current = markers;
	imageForFocusReference.current = image;

	useEffect(() => {
		setImageLoaded(false);
		setScale(1);
		setMarkerPointerActive(false);
		transformStateReference.current = { scale: 1, positionX: 0, positionY: 0 };
	}, [image?.dropboxId, image?.url]);

	useEffect(() => {
		const focusImage = imageForFocusReference.current;
		if (suppressPointerFocusReference.current || !selectedFieldId || !imageLoaded || !focusImage || skipMarkerFocus) {
			return;
		}

		const animationFrame = window.requestAnimationFrame(() => {
			const selectedMarker = markersReference.current.find((marker) => marker.fieldId === selectedFieldId);
			const imageElement = imageReference.current;
			const wrapperElement = transformInstanceReference.current?.wrapperComponent || viewportReference.current;
			const setTransform = setTransformReference.current;
			const transformState = transformStateReference.current;
			if (!selectedMarker || !imageElement || !wrapperElement || !setTransform || !transformState) {
				return;
			}

			const markerPoint = getMarkerScreenPoint(selectedMarker, imageElement);
			const wrapperRectangle = wrapperElement.getBoundingClientRect();
			if (!markerPoint || wrapperRectangle.width <= 0 || wrapperRectangle.height <= 0) {
				return;
			}

			const targetScale = getMarkerFocusTransformScale(focusImage);
			const nextPosition = getZoomCenteredTransformPosition(
				markerPoint.clientX,
				markerPoint.clientY,
				wrapperRectangle,
				transformState.positionX,
				transformState.positionY,
				transformState.scale,
				targetScale,
			);
			setTransform(nextPosition.positionX, nextPosition.positionY, targetScale, MARKER_FOCUS_ANIMATION_MS, "easeOut");
		});

		return () => window.cancelAnimationFrame(animationFrame);
	}, [aerialFocusRequestId, image?.dropboxId, image?.url, imageLoaded, markerFocusRequestId, selectedFieldId, skipMarkerFocus]);

	if (!image) {
		return (
			<div className="relative min-h-[60vh] flex-1 overflow-hidden bg-gray-800">
				<div className="absolute left-3 top-3 z-30 flex gap-2 rounded-md bg-white/90 p-2 shadow">
					<button type="button" className="rounded p-1 hover:bg-gray-200" title="Select aerial image" onClick={onOpenImagePicker}>
						<BsImage />
					</button>
					{readOnly ? null : (
						<button type="button" className="rounded p-1 hover:bg-gray-200" title="Request new aerial image" onClick={onOpenImageRequest}>
							<BsPlusCircle />
						</button>
					)}
				</div>
				<div className="flex min-h-[60vh] flex-1 items-center justify-center p-8 text-center text-white">
					<div>
						<p className="text-xl font-semibold">No aerial image found</p>
						<p className="mt-2 text-sm text-gray-300">Add an image to the configured Dropbox aerial-view folder, or request a new aerial image.</p>
						{readOnly ? null : (
							<button
								type="button"
								className="mt-4 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
								onClick={onOpenImageRequest}>
								Request new aerial image
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}

	const placeMarker = (pointerEvent) => {
		if (!placementFieldId || readOnly) {
			return;
		}

		const coordinates = pointerToNormalizedCoordinates(pointerEvent.clientX, pointerEvent.clientY, imageReference.current);
		if (coordinates) {
			onPlaceMarker(placementFieldId, coordinates);
		}
	};

	return (
		<div ref={viewportReference} className="relative min-h-[60vh] flex-1 overflow-hidden bg-gray-800">
			<TransformWrapper
				key={image.dropboxId || image.url}
				minScale={AERIAL_TRANSFORM_MIN_SCALE}
				maxScale={AERIAL_TRANSFORM_MAX_SCALE}
				centerOnInit
				centerZoomedOut
				limitToBounds
				panning={{ disabled: Boolean(placementFieldId) || markerPointerActive, excluded: ["aerial-marker"] }}
				wheel={{ step: 0.15, excluded: ["aerial-marker"] }}
				pinch={{ excluded: ["aerial-marker"] }}
				onTransformed={(_transformReference, transformState) => {
					transformStateReference.current = transformState;
					setScale(transformState.scale);
				}}>
				{({ zoomIn, zoomOut, resetTransform, setTransform, instance }) => {
					setTransformReference.current = setTransform;
					transformInstanceReference.current = instance;
					return (
						<>
							<div className="absolute left-3 top-3 z-30 flex gap-2 rounded-md bg-white/90 p-2 shadow">
								<button type="button" className="rounded p-1 hover:bg-gray-200" title="Zoom in" onClick={() => zoomIn()}>
									<BsZoomIn />
								</button>
								<button type="button" className="rounded p-1 hover:bg-gray-200" title="Zoom out" onClick={() => zoomOut()}>
									<BsZoomOut />
								</button>
								<button type="button" className="rounded p-1 hover:bg-gray-200" title="Reset view" onClick={() => resetTransform()}>
									<BsArrowsFullscreen />
								</button>
								<button type="button" className="rounded p-1 hover:bg-gray-200" title="Select aerial image" onClick={onOpenImagePicker}>
									<BsImage />
								</button>
								{readOnly ? null : (
									<button type="button" className="rounded p-1 hover:bg-gray-200" title="Request new aerial image" onClick={onOpenImageRequest}>
										<BsPlusCircle />
									</button>
								)}
							</div>
							{placementFieldId ? (
								<div className="absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow">
									Click the image to place the marker
								</div>
							) : null}
							<TransformComponent
								wrapperStyle={{ width: "100%", height: "72vh", minHeight: "60vh" }}
								contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
								<div className={`relative inline-block ${placementFieldId ? "cursor-crosshair" : ""}`} onClick={placeMarker}>
									<img
										ref={imageReference}
										src={image.url}
										alt="Aerial view"
										draggable={false}
										className="block max-h-[72vh] max-w-full select-none object-contain"
										onLoad={() => setImageLoaded(true)}
									/>
									{imageLoaded
										? markers.map((marker) => (
												<AerialMarker
													key={marker.fieldId}
													marker={marker}
													field={fieldsById[marker.fieldId]}
													imageReference={imageReference}
													selected={selectedFieldId === marker.fieldId}
													expanded={expandedFieldId === marker.fieldId}
													scale={scale}
													readOnly={readOnly}
													onSelect={(fieldId) => {
														suppressPointerFocusReference.current = true;
														onSelectField(fieldId);
													}}
													onConfirmSelect={() => {
														suppressPointerFocusReference.current = false;
														onConfirmMarkerSelect();
													}}
													onCancelSelect={() => {
														suppressPointerFocusReference.current = false;
													}}
													onPreview={onPreviewMarker}
													onSave={onSaveMarker}
													onPointerActiveChange={setMarkerPointerActive}
												/>
											))
										: null}
								</div>
							</TransformComponent>
						</>
					);
				}}
			</TransformWrapper>
		</div>
	);
};

export default AerialCanvas;
